import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import { getBaseApiUrl } from '../../../config/api';
import {
  buildEsatSittings,
  buildLnatSittings,
  buildTaraSittings,
  buildTmuaSittings,
  buildUcatSittings,
  sittingNameForSet,
} from '../utils/catalogueSittings';
import { userMayPurchase } from '../../checkout/purchaseEligibility';
import LockedPartNotice, { holdsPaidExam } from './LockedPartNotice';
import usePaperAttemptStatuses from '../hooks/usePaperAttemptStatuses';
import { completedIdsFromStatuses } from '../utils/paperAttemptHelpers';
import { topicAttemptRowFromMap, topicAttemptStatusRequestKey, topicRowAction } from '../utils/topicAttemptRow';
import TmuaWorkspace from './TmuaWorkspace';
import UcatWorkspace from './UcatWorkspace';
import EsatWorkspace from './EsatWorkspace';
import LnatWorkspace from './LnatWorkspace';
import TaraWorkspace from './TaraWorkspace';
import { buildTopicPaperSections } from '../utils/topicPaperSections';
import {
  SECTION_PAPERS_HEADING,
  SECTION_PAPER_DEFAULT_TIER,
  isSectionPaperEntry,
  sectionPaperTitle,
  sectionPaperSitting,
  selectSectionPapers,
} from '../utils/sectionPapers';
import {
  isSubtopicPaperEntry,
  selectSubtopicPapers,
  subtopicPapersOfTier,
  SUBTOPIC_PAPERS_LABEL,
  SUBTOPIC_PAPERS_ALL_PAGE,
  SUBTOPIC_TIER_PAGES,
  SUBTOPIC_PAGE_TITLES,
} from '../utils/subtopicPapers';

const DIFF_CONFIG = {
  easy: { label: 'EASY', dot: 'color-mix(in srgb, var(--iv-head) 30%, transparent)', text: 'var(--iv-body)', bg: 'var(--iv-bg)' },
  medium: { label: 'MEDIUM', dot: 'color-mix(in srgb, var(--iv-head) 60%, transparent)', text: 'var(--iv-body)', bg: 'var(--iv-bg)' },
  hard: { label: 'HARD', dot: 'var(--iv-head)', text: 'var(--iv-body)', bg: 'var(--iv-bg)' },
};

/** Live DB exam names (website Panel_Interview packs) — Q&A practice, not voice interview. */
const MEDINT_SCHOOL_EXAMS = [
  {
    id: 'aston-mmi',
    examName: 'Aston MMI',
    title: 'Aston MMI',
    subtitle: 'Station practice with model answers',
  },
  {
    id: 'birmingham-mmi',
    examName: 'Birmingham MMI',
    title: 'Birmingham MMI',
    subtitle: 'Numeracy, science & stations',
  },
  {
    id: 'cambridge-panel',
    examName: 'Cambridge Medical Panel',
    title: 'Cambridge Panel',
    subtitle: 'Academic panel Q&A',
  },
];

const MEDINT_SCHOOL_EXAM_NAMES = new Set(
  ['Aston MMI', 'Birmingham MMI', 'Cambridge Medical Panel'].map((n) => n.toUpperCase())
);

const isMedicalInterviewsHub = (name) =>
  String(name || '').toUpperCase() === 'MEDICAL INTERVIEWS';

const isMedintSchoolExam = (name) =>
  MEDINT_SCHOOL_EXAM_NAMES.has(String(name || '').toUpperCase());

const isMedintFamilyExam = (name) =>
  isMedicalInterviewsHub(name) || isMedintSchoolExam(name);

const PAID_TIERS = new Set(['foundations', 'beyond']);

// The (user id, exam) each catalogue this page loaded was fetched for. The
// list lives in QuizGeneratorPage, which keeps it while this page unmounts
// (a paper is open), so the owner is kept with the list itself.
const catalogueOwners = new WeakMap();

// These exams render their own workspace, which reads its own statuses.
const WORKSPACE_EXAMS = new Set(['TMUA', 'UCAT', 'LNAT', 'ESAT', 'TARA']);

const ContentSelectorSection = ({
  hideTopBar = false,
  onBack,
  allContent,
  onExamSelected,
  onStartExam,
  onStartSetPaper,
  onStartTopicPaper,
  onContinueTmuaSitting,
  onReviewTmuaSitting,
  onContinueLnatSitting,
  onReviewLnatSitting,
  onContinueTaraSitting,
  onReviewTaraSitting,
  onContinueEsatSitting,
  onReviewEsatSitting,
  onContinueUcatSitting,
  onReviewUcatSitting,
  setCurrentSetPapers,
  onTopicPapersLoaded,
  currentSetPapers,
  currentExamName,
  setCurrentExamName
}) => {
  const { user } = useAuth();
  const mayPurchase = userMayPurchase(user);
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(currentExamName ? 'exam-options' : 'exams'); // 'exams', 'exam-options'

  // Global filter for all sections
  const [globalFilter, setGlobalFilter] = useState('All');

  // Section limits (showcase all by default)
  const [topicLimit, setTopicLimit] = useState(999);
  const [mockLimit, setMockLimit] = useState(999);
  const [pastLimit, setPastLimit] = useState(999);

  // No plan string is read here. Whether a locked part can be bought is
  // decided from what the account owns (access.has_paid, access.owned) inside
  // LockedPartNotice.
  const [showLockedNotice, setShowLockedNotice] = useState(false);
  const [selectedLockedPaper, setSelectedLockedPaper] = useState(null);
  const [livePlans, setLivePlans] = useState({});
  const examPrefix = String(currentExamName || '').toLowerCase();
  // The generic grid's buy control. Hubs do not use this. {} while
  // /api/stripe/plans is loading or has failed, so the button stays hidden.
  const genericExamPurchasable = Boolean(
    livePlans[`${examPrefix}_foundations`] || livePlans[`${examPrefix}_beyond`]
  );

  // The pill on a locked card points to the pricing page. A holder of a paid
  // exam purchase has no upgrade path and an organisation member's plan comes
  // from the organisation, so neither is offered it (the notice still opens).
  const showSeePlans = mayPurchase && !holdsPaidExam(user);

  useEffect(() => {
    fetch(`${getBaseApiUrl()}/api/stripe/plans`)
      .then(res => res.json())
      .then(data => {
        if (data && data.success && data.plans) {
          setLivePlans(data.plans);
        }
      })
      .catch(() => {});
  }, []);

  // A part is locked when the catalogue says so. The server decides it per
  // caller from the part's set feature (the user's purchases, beginnings
  // open), so the page reads no plan
  // string. A catalogue not on paper_sets sends no `locked`: such a part is
  // open only when its tier is (free, beginnings, none), and the start route
  // decides the rest.
  const isPartUnlocked = (paper) => {
    if (typeof paper?.locked === 'boolean') return !paper.locked;
    return !PAID_TIERS.has(String(paper?.tier || '').toLowerCase());
  };

  // A catalogue this page loaded for another account or exam is not shown,
  // from the very render in which the account or exam changed: its locks
  // are that caller's. A list given to the page without an owner is shown.
  const catalogueOwner = `${user?.id || ''}|${currentExamName || ''}`;
  const loadedFor = currentSetPapers ? catalogueOwners.get(currentSetPapers) : undefined;
  const shownSetPapers = loadedFor !== undefined && loadedFor !== catalogueOwner
    ? []
    : (currentSetPapers || []);

  useEffect(() => {
    if (currentExamName) {
      setViewMode('exam-options');
    } else {
      setViewMode('exams');
    }
  }, [currentExamName]);

  // The catalogue's locks are the caller's, so a change of account (or exam)
  // reloads it. A list loaded for another account or exam is dropped first
  // (and is not shown even before that: shownSetPapers), so while the new one
  // loads, and if it fails, no part is shown with another caller's locks; a
  // part with no live decision is locked (isPartUnlocked). The
  // same caller's list is kept while it refreshes, as when a student comes
  // back from a paper. A response that arrives after a newer request is
  // ignored.
  useEffect(() => {
    if (!currentExamName || viewMode !== 'exam-options') return undefined;
    let cancelled = false;
    const owner = catalogueOwner;
    if (loadedFor !== owner) setCurrentSetPapers([]);
    (async () => {
      try {
        const papersResult = await quizGeneratorAPI.getSetPapers(currentExamName);
        if (cancelled) return;
        if (papersResult?.success) {
          const papers = papersResult.papers || [];
          catalogueOwners.set(papers, owner);
          setCurrentSetPapers(papers);
        }
      } catch (error) {
        if (!cancelled) console.error('Error loading exam options:', error?.status ?? null);
      }
    })();
    return () => { cancelled = true; };
    // Runs per (exam, account, view); loadedFor and the callback are read
    // from the render that started the request on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExamName, viewMode, user?.id]);

  // The exam's topic papers, fetched on every visit to its page so a paper the
  // importer has just published shows without a deploy. An empty list means there
  // are none; a failed fetch is its own state and is never shown as empty.
  const [topicState, setTopicState] = useState({ exam: null, papers: [], status: 'loading' });
  useEffect(() => {
    if (!currentExamName || viewMode !== 'exam-options') return undefined;
    let cancelled = false;
    const exam = currentExamName;
    setTopicState({ exam, papers: [], status: 'loading' });
    (async () => {
      try {
        const result = await quizGeneratorAPI.getTopicPapers(exam);
        if (cancelled) return;
        if (!result?.success || !Array.isArray(result.topic_papers)) {
          setTopicState({ exam, papers: [], status: 'error' });
          return;
        }
        setTopicState({ exam, papers: result.topic_papers, status: 'ready' });
        if (onTopicPapersLoaded) onTopicPapersLoaded(result.topic_papers);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading topic papers:', error?.status ?? null);
        setTopicState({ exam, papers: [], status: 'error' });
      }
    })();
    return () => { cancelled = true; };
    // One request per (exam, view): the callback is the render's own, as for the catalogue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExamName, viewMode]);
  // A state fetched for another exam is not shown while this exam's loads.
  const topicPapers = topicState.exam === currentExamName ? topicState.papers : [];
  const topicStatus = topicState.exam === currentExamName ? topicState.status : 'loading';

  const handleExamClick = (examName) => {
    setCurrentExamName(examName);
    setViewMode('exam-options');
    onExamSelected(examName);
  };

  const selectedExam = allContent.find(
    (exam) => String(exam.name || '').toUpperCase() === String(currentExamName || '').toUpperCase()
  );

  const schoolExamLiveCounts = MEDINT_SCHOOL_EXAMS.reduce((acc, school) => {
    const match = (allContent || []).find(
      (exam) => String(exam.name || '').toUpperCase() === school.examName.toUpperCase()
    );
    acc[school.id] = match?.total_available || match?.question_count || null;
    return acc;
  }, {});

  // Dynamic helper: map live API set papers with accurate tier labels, durations, and formats
  const formatLivePaper = (paper) => {
    const catalog = (paper.catalog_id || paper.id || '').toLowerCase();
    const exam = (currentExamName || '').toUpperCase();
    const isSectionB = catalog.includes('section_b') || catalog.endsWith('-sb') || catalog.includes('-sb') || catalog.includes('essay') || paper.section === 'B';
    const isTaraWritingTask = exam === 'TARA' && (
      /[-_]s3\b/.test(catalog)
      || (catalog.startsWith('tsa') && /[-_]s2\b/.test(catalog))
      || isSectionB
      || paper.question_count === 1
    );
    const isEssay = exam === 'TARA' ? isTaraWritingTask : isSectionB;
    // The group a part is shown in comes from its set's level, not from its
    // access: a paper_sets catalogue sends `level` on every part (null for an
    // open past set), and `tier` stays the access label. A catalogue that sends no
    // `level` key (the quiz_papers path) is grouped by its tier, as before.
    const groupLevel = Object.prototype.hasOwnProperty.call(paper, 'level') ? paper.level : paper.tier;
    // A section paper with no level is held to the lower paid tier, not opened to every plan.
    const tier = (groupLevel || (isSectionPaperEntry(paper) || isSubtopicPaperEntry(paper)
      ? SECTION_PAPER_DEFAULT_TIER
      : paper.category === 'past_papers' ? 'free' : 'beginnings')).toLowerCase();
    const tierLabel = tier === 'beyond' ? 'Beyond Tier' : tier === 'foundations' ? 'Foundations Tier' : tier === 'beginnings' ? 'Beginnings Tier' : 'Past Paper';
    const defaultDuration = (exam === 'TARA' || exam === 'ESAT')
      ? '40 min'
      : (isEssay ? '40 min' : (exam === 'TMUA' ? '75 min' : '95 min'));
    const duration = paper.duration_minutes ? `${paper.duration_minutes} min` : defaultDuration;
    const question_count = paper.question_count || (isEssay ? 1 : (exam === 'TARA' ? 22 : 42));

    return {
      ...paper,
      tier,
      tierLabel,
      duration,
      question_count,
      isEssay,
      display_name: paper.display_name || paper.title || paper.id
    };
  };

  // The catalogue in the server's order, each kind on its own. The category
  // decides: a section or subtopic paper is never a mock or a past paper.
  const livePapers = shownSetPapers.map(formatLivePaper);
  const ofCategory = (category) => livePapers.filter(
    (p) => String(p.category || '').toLowerCase() === category
  );
  const pastPapers = ofCategory('past_papers');
  const mockPapers = ofCategory('mock_papers');

  // Standalone single-section papers: their own list, one part each, never
  // built into a sitting.
  const sectionPapers = selectSectionPapers(shownSetPapers, formatLivePaper);
  const subtopicPapers = selectSubtopicPapers(shownSetPapers, formatLivePaper);

  // Card and upgrade-notice title: a section paper's own title, else its
  // set's name as the page names the sitting (on a catalogue with no sets,
  // the paper's own title).
  const formatPaperTitle = (title, paper = {}) => {
    if (isSectionPaperEntry(paper)) return sectionPaperTitle(paper) || String(title || '');
    if (paper.set_title) return sittingNameForSet(currentExamName, paper.set_title);
    return String(title || paper.display_name || paper.id || '');
  };

  // Case study style badge palette for mock tiers
  const getTierBadgeStyle = (tier) => {
    const t = (tier || '').toLowerCase();
    if (t === 'beyond') {
      return {
        bg: 'color-mix(in srgb, var(--iv-accent) 9%, transparent)',
        text: 'var(--iv-accent)',
        border: '1px solid color-mix(in srgb, var(--iv-accent) 20%, transparent)'
      };
    }
    if (t === 'foundations') {
      return {
        bg: 'color-mix(in srgb, var(--iv-accent) 7%, transparent)',
        text: 'var(--iv-body)',
        border: '1px solid var(--iv-line)'
      };
    }
    // Beginnings tier / default (matching case study frosted tag style)
    return {
      bg: 'color-mix(in srgb, var(--iv-surface) 88%, transparent)',
      text: 'var(--iv-body)',
      border: '1px solid var(--iv-line)'
    };
  };

  // Topic practice: the exam's topic papers, in the server's order.
  const displayParams = topicPapers;

  // The generic grid's Completed badges and filter: the signed-in user's
  // statuses from the server for every paper and topic paper it shows.
  const genericStatusKey = WORKSPACE_EXAMS.has(currentExamName)
    ? ''
    : topicAttemptStatusRequestKey([
      ...mockPapers.map((p) => p.id),
      ...pastPapers.map((p) => p.id),
      ...sectionPapers.map((p) => p.id),
      ...displayParams.map((p) => p.paper_id),
    ]);
  const { papers: genericStatuses } = usePaperAttemptStatuses(currentExamName, genericStatusKey);
  const completedList = completedIdsFromStatuses(genericStatuses);

  // Filter for topic papers (they carry no difficulty, so only Completed narrows them)
  const filteredParams = displayParams.filter(param => {
    const isCompleted = completedList.includes(param.paper_id);
    if (globalFilter === 'Completed') return isCompleted;
    return true;
  });

  const sortedParams = filteredParams;
  const visibleParams = sortedParams.slice(0, topicLimit);

  // One topic table per exam page: sections by the paper's own section code, tiers by its level.
  const topicSections = buildTopicPaperSections(currentExamName, displayParams);

  // Filter and sort for Mock Papers
  const filteredMocks = mockPapers.filter(paper => {
    const isCompleted = completedList.includes(paper.id);
    if (globalFilter === 'Completed') return isCompleted;
    if (globalFilter === 'All') return true;
    const diff = paper.difficulty || 'medium';
    return diff.toLowerCase() === globalFilter.toLowerCase();
  });

  const sortedMocks = [...filteredMocks].sort((a, b) => {
    const aComp = completedList.includes(a.id) ? 1 : 0;
    const bComp = completedList.includes(b.id) ? 1 : 0;
    return aComp - bComp;
  });

  const visibleMocks = sortedMocks.slice(0, mockLimit);

  // Filter and sort for Past Papers
  const filteredPast = pastPapers.filter(paper => {
    const isCompleted = completedList.includes(paper.id);
    if (globalFilter === 'Completed') return isCompleted;
    if (globalFilter === 'All') return true;
    const diff = paper.difficulty || 'medium';
    return diff.toLowerCase() === globalFilter.toLowerCase();
  });

  const sortedPast = [...filteredPast].sort((a, b) => {
    const aComp = completedList.includes(a.id) ? 1 : 0;
    const bComp = completedList.includes(b.id) ? 1 : 0;
    return aComp - bComp;
  });

  const visiblePast = sortedPast.slice(0, pastLimit);

  // TMUA hub: artifact-style workspace (in-page nav, no nested routes)
  if (currentExamName === 'TMUA' && viewMode === 'exam-options') {
    const workspaceMocks = buildTmuaSittings(mockPapers);
    const workspacePast = buildTmuaSittings(pastPapers);

    return (
      <>
        <TmuaWorkspace
          onBack={onBack}
          mockSittings={workspaceMocks}
          pastSittings={workspacePast}
          topicSections={topicSections}
          topicStatus={topicStatus}
          isPartUnlocked={isPartUnlocked}
          sectionPapers={sectionPapers}
          subtopicPapers={subtopicPapers}
          onStartSetPaper={onStartSetPaper}
          onStartTopicPaper={onStartTopicPaper}
          onContinueSitting={onContinueTmuaSitting}
          onReviewSitting={onReviewTmuaSitting}
          onRequestUpgrade={(paper) => {
            setSelectedLockedPaper(paper);
            setShowLockedNotice(true);
          }}
        />
        {showLockedNotice && (
          <LockedPartNotice
            title={`Unlock ${selectedLockedPaper ? formatPaperTitle(selectedLockedPaper.display_name, selectedLockedPaper) : 'TMUA Mock Paper'}`}
            tierLabel={selectedLockedPaper?.tierLabel}
            user={user}
            mayPurchase={mayPurchase}
            onSeePlans={() => { setShowLockedNotice(false); navigate('/pricing'); }}
            onClose={() => setShowLockedNotice(false)}
          />
        )}
      </>
    );
  }

  // UCAT hub: artifact-style workspace (in-page nav, no nested routes)
  if (currentExamName === 'UCAT' && viewMode === 'exam-options') {
    const workspaceMocks = buildUcatSittings(mockPapers);
    const workspacePast = buildUcatSittings(pastPapers);

    return (
      <>
        <UcatWorkspace
          onBack={onBack}
          mockSittings={workspaceMocks}
          pastSittings={workspacePast}
          topicSections={topicSections}
          topicStatus={topicStatus}
          isPartUnlocked={isPartUnlocked}
          sectionPapers={sectionPapers}
          subtopicPapers={subtopicPapers}
          onStartSetPaper={onStartSetPaper}
          onStartTopicPaper={onStartTopicPaper}
          onContinueSitting={onContinueUcatSitting}
          onReviewSitting={onReviewUcatSitting}
          onRequestUpgrade={(paper) => {
            setSelectedLockedPaper(paper);
            setShowLockedNotice(true);
          }}
        />
        {showLockedNotice && (
          <LockedPartNotice
            title={`Unlock ${selectedLockedPaper ? formatPaperTitle(selectedLockedPaper.display_name, selectedLockedPaper) : 'UCAT Mock Paper'}`}
            tierLabel={selectedLockedPaper?.tierLabel}
            user={user}
            mayPurchase={mayPurchase}
            onSeePlans={() => { setShowLockedNotice(false); navigate('/pricing'); }}
            onClose={() => setShowLockedNotice(false)}
          />
        )}
      </>
    );
  }

  // LNAT hub: artifact-style workspace (in-page nav, no nested routes)
  if (currentExamName === 'LNAT' && viewMode === 'exam-options') {
    const workspaceMocks = buildLnatSittings(mockPapers);
    const workspacePast = buildLnatSittings(pastPapers);

    return (
      <>
        <LnatWorkspace
          onBack={onBack}
          mockSittings={workspaceMocks}
          pastSittings={workspacePast}
          topicSections={topicSections}
          topicStatus={topicStatus}
          isPartUnlocked={isPartUnlocked}
          sectionPapers={sectionPapers}
          subtopicPapers={subtopicPapers}
          onStartSetPaper={onStartSetPaper}
          onStartTopicPaper={onStartTopicPaper}
          onContinueSitting={onContinueLnatSitting}
          onReviewSitting={onReviewLnatSitting}
          onRequestUpgrade={(paper) => {
            setSelectedLockedPaper(paper);
            setShowLockedNotice(true);
          }}
        />
        {showLockedNotice && (
          <LockedPartNotice
            title={`Unlock ${selectedLockedPaper ? formatPaperTitle(selectedLockedPaper.display_name, selectedLockedPaper) : 'LNAT Mock Paper'}`}
            tierLabel={selectedLockedPaper?.tierLabel}
            user={user}
            mayPurchase={mayPurchase}
            onSeePlans={() => { setShowLockedNotice(false); navigate('/pricing'); }}
            onClose={() => setShowLockedNotice(false)}
          />
        )}
      </>
    );
  }

  // ESAT hub: artifact-style workspace (in-page nav, no nested routes)
  if (currentExamName === 'ESAT' && viewMode === 'exam-options') {
    const workspaceMocks = buildEsatSittings(mockPapers);
    const workspacePast = buildEsatSittings(pastPapers);

    return (
      <>
        <EsatWorkspace
          onBack={onBack}
          mockSittings={workspaceMocks}
          pastSittings={workspacePast}
          topicSections={topicSections}
          topicStatus={topicStatus}
          isPartUnlocked={isPartUnlocked}
          sectionPapers={sectionPapers}
          subtopicPapers={subtopicPapers}
          onStartSetPaper={onStartSetPaper}
          onStartTopicPaper={onStartTopicPaper}
          onContinueSitting={onContinueEsatSitting}
          onReviewSitting={onReviewEsatSitting}
          onRequestUpgrade={(paper) => {
            setSelectedLockedPaper(paper);
            setShowLockedNotice(true);
          }}
        />
        {showLockedNotice && (
          <LockedPartNotice
            title={`Unlock ${selectedLockedPaper ? formatPaperTitle(selectedLockedPaper.display_name, selectedLockedPaper) : 'ESAT Mock Paper'}`}
            tierLabel={selectedLockedPaper?.tierLabel}
            user={user}
            mayPurchase={mayPurchase}
            onSeePlans={() => { setShowLockedNotice(false); navigate('/pricing'); }}
            onClose={() => setShowLockedNotice(false)}
          />
        )}
      </>
    );
  }

  // TARA hub: artifact-style workspace (in-page nav, no nested routes)
  if (currentExamName === 'TARA' && viewMode === 'exam-options') {
    const workspaceMocks = buildTaraSittings(mockPapers);
    const workspacePast = buildTaraSittings(pastPapers);
    return (
      <>
        <TaraWorkspace
          onBack={onBack}
          mockSittings={workspaceMocks}
          pastSittings={workspacePast}
          topicSections={topicSections}
          topicStatus={topicStatus}
          isPartUnlocked={isPartUnlocked}
          sectionPapers={sectionPapers}
          subtopicPapers={subtopicPapers}
          onStartSetPaper={onStartSetPaper}
          onStartTopicPaper={onStartTopicPaper}
          onContinueSitting={onContinueTaraSitting}
          onReviewSitting={onReviewTaraSitting}
          onRequestUpgrade={(paper) => {
            setSelectedLockedPaper(paper);
            setShowLockedNotice(true);
          }}
        />
        {showLockedNotice && (
          <LockedPartNotice
            title={`Unlock ${selectedLockedPaper ? formatPaperTitle(selectedLockedPaper.display_name, selectedLockedPaper) : 'TARA Mock Paper'}`}
            tierLabel={selectedLockedPaper?.tierLabel}
            user={user}
            mayPurchase={mayPurchase}
            onSeePlans={() => { setShowLockedNotice(false); navigate('/pricing'); }}
            onClose={() => setShowLockedNotice(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className="quiz-gen-main-content quiz-gen-main-content-active" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '24px 32px 64px', background: 'var(--iv-raised)' }}>
      <style>{`
        .quiz-unified-back-btn {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: var(--iv-panel);
          border: 1px solid var(--iv-line);
          box-shadow: 0 3px 10px color-mix(in srgb, var(--iv-shadow) 6%, transparent);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--iv-head);
          flex-shrink: 0;
          transition: all 0.2s ease;
          padding: 0;
        }
        .quiz-unified-back-btn:hover {
          background: var(--iv-surface);
          transform: scale(1.04);
          box-shadow: 0 4px 14px color-mix(in srgb, var(--iv-shadow) 10%, transparent);
        }
        .content-selector-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          width: 100%;
          box-sizing: border-box;
        }
        @media (max-width: 1100px) {
          .content-selector-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 14px !important;
          }
        }
        @media (max-width: 768px) {
          .content-selector-grid {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            gap: 14px !important;
          }
          .quiz-gen-main-content {
            padding: 16px 16px 40px !important;
            overflow-x: hidden !important;
          }
          .content-selector-grid > div {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
        }
      `}</style>

      {/* ===== HEADER & TITLE SECTION ===== */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', padding: '0 0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingLeft: '52px' }}>
          <h1 style={{ fontFamily: 'var(--iv-font-head)', fontWeight: 700, fontSize: '22px', lineHeight: 1.1, letterSpacing: '-0.015em', margin: 0, color: 'var(--iv-head)' }}>
            {viewMode === 'exams' ? 'Practice Exams' : `${selectedExam?.display_name || currentExamName} Prep`}
          </h1>
        </div>

        {viewMode !== 'exams' && currentExamName !== 'TMUA' && currentExamName !== 'LNAT' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--iv-raised)', border: '1px solid var(--iv-line)', borderRadius: '999px', padding: '5px', boxShadow: '0 4px 12px color-mix(in srgb, var(--iv-shadow) 2%, transparent)' }}>
            {['All', 'Completed'].map(diff => {
              const on = globalFilter === diff;
              const conf = DIFF_CONFIG[diff.toLowerCase()];
              return (
                <span
                  key={diff}
                  onClick={() => {
                    setGlobalFilter(diff);
                    setTopicLimit(10);
                    setMockLimit(10);
                    setPastLimit(10);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    fontFamily: 'var(--iv-font-body)',
                    fontSize: '13px',
                    fontWeight: '600',
                    letterSpacing: '0.01em',
                    color: on ? 'var(--iv-on-band)' : 'var(--iv-muted)',
                    background: on ? 'var(--iv-band)' : 'transparent',
                    padding: '8px 18px',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {conf && <span style={{ display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%', background: conf.dot }}></span>}
                  {diff}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {viewMode === 'exams' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {allContent.map(exam => (
            <div
              key={exam.id}
              onClick={() => handleExamClick(exam.name)}
              style={{
                position: 'relative',
                background: 'var(--iv-bg)',
                border: '1px solid var(--iv-line)',
                borderRadius: '12px',
                padding: '18px 20px',
                boxShadow: '0 2px 8px color-mix(in srgb, var(--iv-shadow) 5%, transparent)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '120px'
              }}
            >
              <div>
                <div style={{ fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em', color: 'var(--iv-accent)', marginBottom: '8px' }}>EXAM</div>
                <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: '20px', fontWeight: '700', color: 'var(--iv-body)' }}>{exam.display_name}</div>
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--iv-muted)', marginTop: '12px' }}>{exam.duration || 90} mins · {exam.num_questions || 20} questions</div>
            </div>
          ))}
          {[
            'STEP – Sixth Term Examination Paper in Mathematics',
            'PAT – Physics Admissions Test',
            'CSAT – Computer Science Aptitudes Test',
            'AHCAAT – Ancient History and Classical Archaeology Admissions Test',
            'BMSAT – Biomedical Sciences Admissions Test',
            'CAT – Classics Admissions Test',
            'PhilAT – Philosophy Admissions Test'
          ].map((examName, index) => (
            <div
              key={`locked-${index}`}
              style={{
                position: 'relative',
                background: 'var(--iv-fill)',
                border: '1px solid var(--iv-line)',
                borderRadius: '12px',
                padding: '18px 20px',
                opacity: 0.6,
                cursor: 'not-allowed',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '136px'
              }}
            >
              <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: '16px', fontWeight: '600', color: 'var(--iv-muted)', lineHeight: '1.2', marginBottom: '12px' }}>
                {examName}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="11" width="14" height="9" rx="2" />
                  <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'exam-options' && (
        <>
          {/* =================================================================== */}
          {/* ===== MEDICAL INTERVIEWS — SCHOOL EXAMS (Q&A) ===================== */}
          {/* =================================================================== */}
          {isMedicalInterviewsHub(currentExamName) && (
            <div style={{ marginBottom: '40px' }}>
              <div style={{ marginBottom: '14px' }}>
                <h2 style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '18px', margin: 0, color: 'var(--iv-head)', letterSpacing: '-0.02em' }}>
                  School banks
                </h2>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--iv-muted)', lineHeight: 1.4 }}>
                  Written Q&amp;A with model answers
                </p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
                {MEDINT_SCHOOL_EXAMS.map((fmt) => {
                  const qCount = schoolExamLiveCounts[fmt.id];
                  return (
                    <div
                      key={fmt.id}
                      onClick={() => navigate(`/practice/interviews/${encodeURIComponent(fmt.examName)}`)}
                      style={{
                        background: 'var(--iv-surface)',
                        border: '1px solid var(--iv-line)',
                        borderRadius: '10px',
                        padding: '16px 18px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        minHeight: '108px',
                        transition: 'border-color 0.15s ease',
                      }}
                    >
                      <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: '16px', fontWeight: 600, color: 'var(--iv-body)', lineHeight: 1.25 }}>
                        {fmt.title}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--iv-muted)', lineHeight: 1.4, flex: 1 }}>
                        {fmt.subtitle}
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--iv-accent)' }}>
                        {qCount != null ? `${qCount} questions` : 'Open'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isMedintSchoolExam(currentExamName) && (
            <div style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => navigate('/practice/interviews/Medical%20Interviews')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--iv-accent)',
                  cursor: 'pointer',
                }}
              >
                ← Medical Interviews
              </button>
            </div>
          )}

          {/* =================================================================== */}
          {/* ===== 1. PRACTICE BY TOPIC ======================================== */}
          {/* =================================================================== */}
          {(visibleParams.length > 0 || topicStatus !== 'loading') && (
            <div style={{ marginBottom: '44px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: isMedintFamilyExam(currentExamName) ? '14px' : '18px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <h2 style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: isMedintFamilyExam(currentExamName) ? '18px' : '19px', margin: 0, color: 'var(--iv-head)', letterSpacing: '-0.02em' }}>
                    {isMedicalInterviewsHub(currentExamName)
                      ? 'By topic'
                      : isMedintSchoolExam(currentExamName)
                        ? 'Practice sets'
                        : 'Topic Practice'}
                  </h2>
                  {isMedicalInterviewsHub(currentExamName) && (
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--iv-muted)', lineHeight: 1.4 }}>
                      Motivation, ethics, science, current affairs, professionalism
                    </p>
                  )}
                  {isMedintSchoolExam(currentExamName) && (
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--iv-muted)', lineHeight: 1.4 }}>
                      Each set includes questions and model answers
                    </p>
                  )}
                </div>
              </div>

              {topicStatus === 'error' ? (
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--iv-danger)' }}>
                  Practice topics could not be loaded. Please try again.
                </p>
              ) : displayParams.length === 0 ? (
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--iv-muted)' }}>
                  Practice topics are currently unavailable.
                </p>
              ) : isMedintFamilyExam(currentExamName) ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                  {visibleParams.map((param) => {
                    const isCompleted = completedList.includes(param.paper_id);
                    const action = topicRowAction(topicAttemptRowFromMap(genericStatuses, param.paper_id), isCompleted);
                    const qCount = param.question_count;
                    return (
                      <div
                        key={param.paper_id}
                        onClick={() => onStartTopicPaper(currentExamName, param, {
                          forceNew: action.forceNew,
                          resume: action.resume,
                        })}
                        style={{
                          background: 'var(--iv-surface)',
                          border: `1px solid ${isCompleted ? 'color-mix(in srgb, var(--iv-success) 30%, transparent)' : 'var(--iv-line)'}`,
                          borderRadius: '10px',
                          padding: '16px 18px',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          minHeight: '104px',
                        }}
                      >
                        <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: '15.5px', fontWeight: 600, color: 'var(--iv-body)', lineHeight: 1.3 }}>
                          {param.title}
                        </div>
                        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                          <span style={{ fontSize: '12.5px', color: 'var(--iv-muted)' }}>
                            {qCount == null
                              ? '—'
                              : `${qCount} question${Number(qCount) === 1 ? '' : 's'}`}
                          </span>
                          {(isCompleted || action.resume) && (
                            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--iv-body)' }}>
                              {action.resume ? 'Resume' : 'Done'}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                    {visibleParams.map(param => {
                      const isCompleted = completedList.includes(param.paper_id);
                      const action = topicRowAction(topicAttemptRowFromMap(genericStatuses, param.paper_id), isCompleted);
                      const border = isCompleted ? 'var(--iv-success)' : 'var(--iv-line)';

                      return (
                        <div
                          key={param.paper_id}
                          onClick={() => onStartTopicPaper(currentExamName, param, {
                            forceNew: action.forceNew,
                            resume: action.resume,
                          })}
                          style={{
                            position: 'relative',
                            background: 'var(--iv-bg)',
                            border: `1.5px solid ${border}`,
                            borderRadius: '12px',
                            padding: '18px 20px',
                            boxShadow: '0 2px 8px color-mix(in srgb, var(--iv-shadow) 5%, transparent)',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            minHeight: '136px'
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '9px' }}>
                              <div style={{ fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em', color: 'var(--iv-head)' }}>
                                {param.paper_id}
                              </div>
                              {isCompleted && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.06em', color: 'var(--iv-success)', background: 'var(--iv-bg)', padding: '3px 9px', borderRadius: '999px' }}>
                                  ✓ COMPLETED
                                </span>
                              )}
                              {!isCompleted && action.resume && (
                                <span style={{ fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.06em', color: 'var(--iv-body)' }}>
                                  RESUME
                                </span>
                              )}
                            </div>
                            <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: '18px', fontWeight: '600', color: 'var(--iv-head)', lineHeight: '1.2', margin: '0 0 14px' }}>
                              {param.title}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <span style={{ fontSize: '12.5px', fontWeight: '500', color: 'var(--iv-muted)', fontFamily: 'var(--iv-font-body)' }}>
                              {param.question_count} question{param.question_count === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {sortedParams.length > topicLimit && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                      <button
                        onClick={() => setTopicLimit(prev => prev + 12)}
                        style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontFamily: 'var(--iv-font-body)', fontSize: '13.5px', fontWeight: '600', color: 'var(--iv-body)' }}
                      >
                        MORE
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* =================================================================== */}
          {/* ===== 2. MOCK PAPERS ============================================== */}
          {/* =================================================================== */}
          {!isMedintFamilyExam(currentExamName) && (
          <div style={{ marginBottom: '44px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '18px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2 style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '19px', margin: 0, color: 'var(--iv-head)', letterSpacing: '-0.02em' }}>Mock Papers</h2>
              </div>
            </div>

            {visibleMocks.length > 0 ? (
              <>
                <div className="content-selector-grid">
                  {visibleMocks.map(paper => {
                    const isEssay = (Boolean(paper.isEssay)
                      || paper.id.endsWith('-sb')
                      || paper.id.includes('essay')
                      || (String(currentExamName).toUpperCase() === 'TARA' && /[-_]s3\b/i.test(paper.id))
                      || (String(currentExamName).toUpperCase() === 'TARA' && paper.question_count === 1));
                    const essayLabel = String(currentExamName).toUpperCase() === 'TARA' ? 'Writing Task' : 'Essay';
                    const durationLabel = paper.duration
                      || (String(currentExamName).toUpperCase() === 'TARA' || String(currentExamName).toUpperCase() === 'ESAT'
                        ? '40 min'
                        : (isEssay ? '40 min' : '95 min'));
                    const diff = paper.difficulty || 'medium';
                    const conf = DIFF_CONFIG[diff] || DIFF_CONFIG.medium;
                    const isCompleted = completedList.includes(paper.id);
                    const unlocked = isPartUnlocked(paper);
                    const title = formatPaperTitle(paper.display_name, paper);
                    const questionCount = paper.question_count;

                    return (
                      <div
                        key={paper.catalog_id || paper.id}
                        onClick={() => {
                          if (unlocked) {
                            onStartSetPaper(
                              currentExamName,
                              paper.id,
                              paper.catalog_id,
                              null
                            );
                          } else if (genericExamPurchasable) {
                            setSelectedLockedPaper(paper);
                            setShowLockedNotice(true);
                          }
                        }}
                        style={{
                          position: 'relative',
                          background: unlocked ? 'var(--iv-bg)' : 'var(--iv-bg)',
                          border: isCompleted ? '1.5px solid var(--iv-line)' : unlocked ? '1px solid var(--iv-line)' : '1px solid var(--iv-line)',
                          borderRadius: '12px',
                          padding: '18px 20px',
                          boxShadow: '0 2px 8px color-mix(in srgb, var(--iv-shadow) 5%, transparent)',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          minHeight: '136px',
                          overflow: 'hidden'
                        }}
                      >
                        {!unlocked && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: '12px',
                            background: 'color-mix(in srgb, var(--iv-surface) 72%, transparent)',
                            backdropFilter: 'blur(3.5px)',
                            WebkitBackdropFilter: 'blur(3.5px)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            padding: '12px',
                            zIndex: 2,
                            transition: 'all 0.2s ease'
                          }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--iv-body)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="5" y="11" width="14" height="10" rx="2" />
                              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                            </svg>
                            <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.05em', color: 'var(--iv-body)', textTransform: 'uppercase', textAlign: 'center', fontFamily: 'var(--iv-font-body)' }}>
                              {paper.tierLabel || 'LOCKED TIER'}
                            </span>
                            {genericExamPurchasable && showSeePlans && (
                            <span style={{ fontSize: '10.5px', fontWeight: '700', color: 'var(--iv-on-band)', background: 'var(--iv-band)', padding: '4px 10px', borderRadius: '999px', fontFamily: 'var(--iv-font-body)', boxShadow: '0 2px 6px color-mix(in srgb, var(--iv-shadow) 12%, transparent)' }}>
                              See plans
                            </span>
                            )}
                          </div>
                        )}

                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                            {isCompleted ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.06em', color: 'var(--iv-success)', background: 'var(--iv-bg)', padding: '3px 9px', borderRadius: '999px' }}>
                                ✓ COMPLETED
                              </span>
                            ) : !unlocked ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.06em', color: 'var(--iv-muted)', background: 'var(--iv-bg)', padding: '3px 9px', borderRadius: '999px' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                                {paper.tierLabel || 'LOCKED'}
                              </span>
                            ) : (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                fontSize: '9.5px',
                                fontWeight: '600',
                                letterSpacing: '0.03em',
                                color: getTierBadgeStyle(paper.tier).text,
                                background: getTierBadgeStyle(paper.tier).bg,
                                border: getTierBadgeStyle(paper.tier).border,
                                padding: '2.5px 8.5px',
                                borderRadius: '999px',
                                fontFamily: 'var(--iv-font-body)'
                              }}>
                                {paper.tierLabel || 'Beginnings Tier'}
                              </span>
                            )}
                          </div>
                          <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: '18px', fontWeight: '600', color: 'var(--iv-body)', lineHeight: '1.2', marginBottom: '12px' }}>
                            {title}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: '500', color: 'var(--iv-muted)', fontFamily: 'var(--iv-font-body)' }}>
                            {isEssay ? (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 19l1-4L16 5l3 3L9 18l-4 1z" />
                                </svg>
                                {essayLabel}
                              </>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M7 3h7l4 4v14H7z" />
                                  <polyline points="14,3 14,7 18,7" />
                                </svg>
                                {questionCount} questions
                              </>
                            )}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: '500', color: 'var(--iv-muted)', fontFamily: 'var(--iv-font-body)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="8" />
                              <polyline points="12,8 12,12 15,14" />
                            </svg>
                            {durationLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {sortedMocks.length > mockLimit && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                    <button
                      onClick={() => setMockLimit(prev => prev + 12)}
                      style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontFamily: 'var(--iv-font-body)', fontSize: '13.5px', fontWeight: '600', color: 'var(--iv-body)' }}
                    >
                      MORE
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
          )}

          {/* =================================================================== */}
          {/* ===== 2b. SECTION PRACTICE ======================================== */}
          {/* Standalone single-section papers, below the mocks and never paired.  */}
          {/* =================================================================== */}
          {!isMedintFamilyExam(currentExamName) && sectionPapers.length > 0 && (
          <div style={{ marginBottom: '44px' }} data-testid="section-practice">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <h2 style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '19px', margin: 0, color: 'var(--iv-head)', letterSpacing: '-0.02em' }}>{SECTION_PAPERS_HEADING}</h2>
            </div>
            <div className="content-selector-grid">
              {sectionPapers
                .filter((paper) => {
                  if (globalFilter === 'Completed') return completedList.includes(paper.id);
                  if (globalFilter === 'All') return true;
                  return String(paper.difficulty || 'medium').toLowerCase() === globalFilter.toLowerCase();
                })
                .map((paper) => {
                  const unlocked = isPartUnlocked(paper);
                  const isCompleted = completedList.includes(paper.id);
                  return (
                    <div
                      key={paper.catalog_id || paper.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (unlocked) {
                          onStartSetPaper(currentExamName, paper.id, paper.catalog_id || paper.id, sectionPaperSitting(currentExamName, paper));
                        } else if (genericExamPurchasable) {
                          setSelectedLockedPaper(paper);
                          setShowLockedNotice(true);
                        }
                      }}
                      style={{
                        position: 'relative',
                        background: unlocked ? 'var(--iv-raised)' : 'var(--iv-raised)',
                        border: isCompleted ? '1.5px solid var(--iv-line)' : unlocked ? '1px solid var(--iv-line)' : '1px solid var(--iv-line)',
                        borderRadius: '12px',
                        padding: '18px 20px',
                        boxShadow: '0 2px 8px color-mix(in srgb, var(--iv-shadow) 5%, transparent)',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minHeight: '136px'
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: '9.5px',
                            fontWeight: '600',
                            letterSpacing: '0.03em',
                            color: getTierBadgeStyle(paper.tier).text,
                            background: getTierBadgeStyle(paper.tier).bg,
                            border: getTierBadgeStyle(paper.tier).border,
                            padding: '2.5px 8.5px',
                            borderRadius: '999px',
                            fontFamily: 'var(--iv-font-body)'
                          }}>
                            {unlocked ? paper.tierLabel : `${paper.tierLabel} · Locked`}
                          </span>
                          {isCompleted && (
                            <span style={{ fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.06em', color: 'var(--iv-success)', background: 'var(--iv-bg)', padding: '3px 9px', borderRadius: '999px', fontFamily: 'var(--iv-font-body)' }}>
                              ✓ COMPLETED
                            </span>
                          )}
                        </div>
                        <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: '18px', fontWeight: '600', color: 'var(--iv-body)', lineHeight: '1.2', marginBottom: '12px' }}>
                          {formatPaperTitle(paper.display_name, paper)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '12.5px', fontWeight: '500', color: 'var(--iv-muted)', fontFamily: 'var(--iv-font-body)' }}>
                        <span>{paper.question_count} questions</span>
                        <span>{paper.duration}</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          )}

          {/* =================================================================== */}
          {/* ===== 3. PAST PAPERS ============================================== */}
          {/* =================================================================== */}
          {!isMedintFamilyExam(currentExamName) && (
          <div style={{ marginBottom: '44px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2 style={{ fontFamily: 'var(--iv-font-body)', fontWeight: 700, fontSize: '19px', margin: 0, color: 'var(--iv-head)', letterSpacing: '-0.02em' }}>Past Papers</h2>
              </div>
            </div>

            {visiblePast.length > 0 ? (
              <>
                <div className="content-selector-grid">
                  {visiblePast.map(paper => {
                    const isEssay = (Boolean(paper.isEssay)
                      || paper.id.endsWith('-sb')
                      || paper.id.includes('section_b')
                      || paper.id.includes('section_B')
                      || paper.id.includes('essay')
                      || (String(currentExamName).toUpperCase() === 'TARA' && /[-_]s3\b/i.test(paper.id))
                      || (String(currentExamName).toUpperCase() === 'TARA' && (/[-_]s2\b/i.test(paper.id) && String(paper.id).toLowerCase().startsWith('tsa') || paper.question_count === 1)));
                    const essayLabel = String(currentExamName).toUpperCase() === 'TARA' ? 'Writing Task' : 'Essay';
                    const durationLabel = paper.duration
                      || (String(currentExamName).toUpperCase() === 'TARA' || String(currentExamName).toUpperCase() === 'ESAT'
                        ? '40 min'
                        : (isEssay ? '40 min' : '95 min'));
                    const yearMatch = paper.display_name?.match(/20\d\d/) || paper.id?.match(/20\d\d/);
                    const year = yearMatch ? yearMatch[0] : (paper.display_name?.includes('Sample') || paper.id?.includes('sample') ? 'SAMPLE' : 'PAST');
                    const isCompleted = completedList.includes(paper.id);
                    const title = formatPaperTitle(paper.display_name, paper);
                    const questionCount = paper.question_count;

                    return (
                      <div
                        key={paper.id}
                        onClick={() => onStartSetPaper(
                          currentExamName,
                          paper.id,
                          paper.catalog_id,
                          null
                        )}
                        style={{
                          position: 'relative',
                          background: 'var(--iv-bg)',
                          border: isCompleted ? '1.5px solid var(--iv-line)' : '1px solid var(--iv-line)',
                          borderRadius: '12px',
                          padding: '18px 20px',
                          boxShadow: '0 2px 8px color-mix(in srgb, var(--iv-shadow) 5%, transparent)',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          minHeight: '136px'
                        }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                            <div style={{ fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em', color: 'var(--iv-muted)', fontFamily: 'var(--iv-font-body)' }}>
                              {year}
                            </div>
                            {isCompleted ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.06em', color: 'var(--iv-success)', background: 'var(--iv-bg)', padding: '3px 9px', borderRadius: '999px' }}>
                                ✓ COMPLETED
                              </span>
                            ) : (
                              <span style={{ fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.06em', color: 'var(--iv-body)', background: 'var(--iv-bg)', padding: '3px 9px', borderRadius: '999px', fontFamily: 'var(--iv-font-body)' }}>
                                TIMED
                              </span>
                            )}
                          </div>
                          <div style={{ fontFamily: 'var(--iv-font-body)', fontSize: '18px', fontWeight: '600', color: 'var(--iv-body)', lineHeight: '1.2', marginBottom: '12px' }}>
                            {title}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: '500', color: 'var(--iv-muted)', fontFamily: 'var(--iv-font-body)' }}>
                            {isEssay ? (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 19l1-4L16 5l3 3L9 18l-4 1z" />
                                </svg>
                                {essayLabel}
                              </>
                            ) : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M7 3h7l4 4v14H7z" />
                                  <polyline points="14,3 14,7 18,7" />
                                </svg>
                                {questionCount} questions
                              </>
                            )}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: '500', color: 'var(--iv-muted)', fontFamily: 'var(--iv-font-body)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iv-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="8" />
                              <polyline points="12,8 12,12 15,14" />
                            </svg>
                            {durationLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {sortedPast.length > pastLimit && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                    <button
                      onClick={() => setPastLimit(prev => prev + 12)}
                      style={{ background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontFamily: 'var(--iv-font-body)', fontSize: '13.5px', fontWeight: '600', color: 'var(--iv-body)' }}
                    >
                      MORE
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: '32px', textAlign: 'center', background: 'var(--iv-bg)', borderRadius: '12px', border: '1px solid var(--iv-line)', color: 'var(--iv-muted)', fontFamily: 'var(--iv-font-body)' }}>
                No past papers found.
              </div>
            )}
          </div>
          )}

          {/* Full Practice Test Button (Disabled) */}
          {false && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '36px' }}>
              <div
                onClick={() => onStartExam(currentExamName)}
                style={{
                  background: 'var(--iv-btn)',
                  color: 'var(--iv-btn-fg)',
                  borderRadius: '8px',
                  padding: '14px 28px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px color-mix(in srgb, var(--iv-shadow) 15%, transparent)',
                  fontFamily: 'var(--iv-font-body)',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textAlign: 'center'
                }}
              >
                Full Practice Test ({selectedExam?.duration || 90} minutes · {selectedExam?.num_questions || 20} questions)
              </div>
            </div>
          )}
        </>
      )}

      {showLockedNotice && (
        <LockedPartNotice
          title={`Unlock ${selectedLockedPaper ? formatPaperTitle(selectedLockedPaper.display_name, selectedLockedPaper) : `${(currentExamName || 'LNAT').toUpperCase()} Mock Paper`}`}
          tierLabel={selectedLockedPaper?.tierLabel}
          user={user}
          mayPurchase={mayPurchase}
          onSeePlans={() => { setShowLockedNotice(false); navigate('/pricing'); }}
          onClose={() => setShowLockedNotice(false)}
        />
      )}
    </div>
  );
};

export default ContentSelectorSection;
